import { t } from "@/lib/i18n/interpolate";
import {
  asSentence,
  authorLabel,
  type NameStyle,
  stripFinalPeriod,
} from "@/lib/tool-kit/publishing/authors";

/**
 * Competing-interest (conflict-of-interest) statements.
 *
 * The interest types follow the ICMJE Disclosure Form (2021) and the publishers'
 * definitions built on it: research support, employment, consulting fees, honoraria,
 * expert testimony, travel support, patents, royalties, advisory and monitoring boards,
 * leadership roles, stock, and the non-financial interests — personal relationships and
 * anything else a reader would want to know.
 *
 * Two published wordings are offered: the standard sentences Nature Portfolio prints
 * ("The authors declare no competing interests." / "The authors declare the following
 * competing interests:") and the two options of Elsevier's declaration-of-interest
 * template.
 */

export const INTEREST_TYPES = [
  "funding",
  "employment",
  "pastEmployment",
  "consulting",
  "honoraria",
  "stock",
  "patent",
  "royalties",
  "advisory",
  "testimony",
  "travel",
  "leadership",
  "personal",
  "other",
] as const;

export type InterestType = (typeof INTEREST_TYPES)[number];

export type InterestStyle = "nature" | "elsevier";

export interface Declaration {
  type: InterestType;
  /** The organization, or the patent / relationship / interest being described. */
  entity: string;
  details: string;
  /** "outside the submitted work" — ICMJE's distinction. */
  outside: boolean;
}

export interface AuthorInterests {
  name: string;
  declarations: readonly Declaration[];
}

export interface InterestTemplates {
  sentences: Readonly<Record<InterestType, string>>;
  details: string;
  outside: string;
  placeholderEntity: string;
}

/** Plural-sensitive sentences, already chosen for the author counts by the widget. */
export interface InterestFrame {
  /** "The authors declare no competing interests." */
  none: string;
  /** "The authors declare the following competing interests:" */
  intro: string;
  /** "The remaining authors declare no competing interests." */
  remaining: string;
}

function named(authors: readonly AuthorInterests[]) {
  return authors.filter((author) => author.name.trim() !== "");
}

/** Named authors who declare at least one interest, and those who declare none. */
export function interestCounts(authors: readonly AuthorInterests[]): {
  authors: number;
  declaring: number;
  remaining: number;
} {
  const people = named(authors);
  const declaring = people.filter((a) => a.declarations.length > 0).length;
  return {
    authors: people.length,
    declaring,
    remaining: people.length - declaring,
  };
}

export function declarationSentence(
  name: string,
  declaration: Declaration,
  templates: InterestTemplates
): string {
  const entity = stripFinalPeriod(declaration.entity);
  const details = stripFinalPeriod(declaration.details);
  const base = t(templates.sentences[declaration.type], {
    name,
    entity: entity === "" ? templates.placeholderEntity : entity,
  });
  const withDetails =
    details === "" ? base : `${base}${t(templates.details, { details })}`;
  return asSentence(
    declaration.outside ? `${withDetails}${templates.outside}` : withDetails
  );
}

/** Every declaration as a sentence, author by author, in the order entered. */
export function declarationSentences(
  authors: readonly AuthorInterests[],
  nameStyle: NameStyle,
  templates: InterestTemplates
): string[] {
  return named(authors).flatMap((author) =>
    author.declarations.map((declaration) =>
      declarationSentence(
        authorLabel(author.name, nameStyle),
        declaration,
        templates
      )
    )
  );
}

/**
 * The statement: the "none" sentence when nobody declares anything; otherwise the
 * introduction, each declaration, and — when some authors have nothing to declare —
 * a closing sentence saying so.
 */
export function interestStatement(
  authors: readonly AuthorInterests[],
  nameStyle: NameStyle,
  templates: InterestTemplates,
  frame: InterestFrame
): string {
  const sentences = declarationSentences(authors, nameStyle, templates);
  if (sentences.length === 0) {
    return frame.none;
  }
  const { remaining } = interestCounts(authors);
  return [
    frame.intro,
    ...sentences,
    ...(remaining > 0 ? [frame.remaining] : []),
  ].join(" ");
}
